/**
 * API service layer for data fetching.
 */

import { fetchAuthSession } from 'aws-amplify/auth';
import type {
  Account,
  AccountSummary,
  Position,
  PositionsResponse,
  WashSalesResponse,
} from '@/types';

/** API base URL from environment */
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

/** Default retry configuration */
const DEFAULT_RETRIES = 3;
const RETRY_DELAY_MS = 1000;

/**
 * Custom API error with status code.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Sleep for specified milliseconds.
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Get auth token for API requests.
 */
async function getAuthToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() ?? null;
  } catch {
    return null;
  }
}

/**
 * Fetch wrapper with auth, error handling, and retry logic.
 */
async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {},
  retries: number = DEFAULT_RETRIES
): Promise<T> {
  const token = await getAuthToken();
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }
  
  const url = `${API_BASE_URL}${endpoint}`;
  
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });
      
      if (!response.ok) {
        const errorBody = await response.text();
        throw new ApiError(
          errorBody || `HTTP ${response.status}`,
          response.status
        );
      }
      
      return await response.json();
    } catch (error) {
      if (error instanceof ApiError) {
        // Don't retry client errors (4xx)
        if (error.status >= 400 && error.status < 500) {
          throw error;
        }
      }
      
      // Retry on network errors or server errors
      if (attempt < retries) {
        await sleep(RETRY_DELAY_MS * attempt);
        continue;
      }
      
      throw error;
    }
  }
  
  throw new ApiError('Max retries exceeded', 0);
}

// ============================================================================
// Account API
// ============================================================================

/**
 * Fetch all accounts.
 */
export async function fetchAccounts(): Promise<Account[]> {
  const response = await apiFetch<{ accounts: Account[] }>('/accounts');
  return response.accounts;
}

/**
 * Fetch account summary by ID.
 */
export async function fetchAccountSummary(
  accountId: string
): Promise<AccountSummary | null> {
  const response = await apiFetch<{ summary: AccountSummary }>(
    `/accounts/${accountId}`
  );
  return response.summary;
}

// ============================================================================
// Position API
// ============================================================================

/**
 * Fetch positions for an account with pagination and filtering support.
 */
export async function fetchPositions(
  accountId: string,
  options?: { limit?: number; lastEvaluatedKey?: string | null; status?: 'ACTIVE' | 'CLOSED' }
): Promise<PositionsResponse> {
  const params = new URLSearchParams();
  if (options?.limit) {
    params.set('limit', String(options.limit));
  }
  if (options?.lastEvaluatedKey) {
    params.set('last_evaluated_key', options.lastEvaluatedKey);
  }
  if (options?.status) {
    params.set('status', options.status);
  }
  const query = params.toString();
  const endpoint = `/accounts/${accountId}/positions${query ? `?${query}` : ''}`;
  return apiFetch<PositionsResponse>(endpoint);
}

/**
 * Fetch a single position.
 */
export async function fetchPosition(
  positionId: string
): Promise<Position | null> {
  const response = await apiFetch<{ position: Position }>(
    `/positions/${positionId}`
  );
  return response.position;
}

// ============================================================================
// Performance API
// ============================================================================

export interface PerformanceDataPoint {
  date: string;
  value: number;
  pnl: number;
}

/**
 * Fetch historical performance data.
 */
export async function fetchPerformance(
  accountId: string,
  days: number = 30
): Promise<PerformanceDataPoint[]> {
  const response = await apiFetch<{ data: PerformanceDataPoint[] }>(
    `/accounts/${accountId}/performance?days=${days}`
  );
  return response.data;
}

// ============================================================================
// Account Settings API
// ============================================================================

export interface AccountSettings {
  /** Effective NLV: live value from Tastytrade when available, else the manual value. */
  nlv: number | null;
  /** Timestamp for the effective NLV (live fetched_at or manual updated_at). */
  nlv_updated_at: string | null;
  /** Where the effective NLV came from. */
  nlv_source?: 'tastytrade' | 'manual' | null;
  /** The manually entered NLV, always preserved so the override stays editable. */
  manual_nlv?: number | null;
  manual_nlv_updated_at?: string | null;
  /** True when the effective NLV was read live from Tastytrade. */
  live_nlv_available?: boolean;
}

/**
 * Fetch account settings (including NLV).
 */
export async function fetchAccountSettings(
  accountId: string
): Promise<AccountSettings> {
  const response = await apiFetch<{ settings: AccountSettings }>(
    `/accounts/${accountId}/settings`
  );
  return response.settings;
}

/**
 * Update account NLV.
 */
export async function updateAccountNlv(
  accountId: string,
  nlv: number
): Promise<AccountSettings> {
  const response = await apiFetch<{ settings: AccountSettings }>(
    `/accounts/${accountId}/settings`,
    {
      method: 'PUT',
      body: JSON.stringify({ nlv }),
    }
  );
  return response.settings;
}

// ============================================================================
// Wash Sales API
// ============================================================================

/**
 * Fetch active wash sale restrictions for an account.
 */
export async function fetchWashSales(
  accountId: string
): Promise<WashSalesResponse> {
  return apiFetch<WashSalesResponse>(`/accounts/${accountId}/wash-sales`);
}
